import { Pencil, Trash2, ClipboardCheck, Link } from "lucide-react";

import ExpandableTable from "@/components/desktop/list/table/ExpandableTable";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import useFilterStore from "@/stores/useFilterStore";

import { convertValue } from "@/utils/unitConverter";
import { getUnitLabel } from "@/utils/unitFormatter";
import { formatNumber, truncateToDecimals } from "@/utils/numberFormat";
import { formatUser } from "@/utils/userFormatter";
import { formatNameWithTooltip } from "@/utils/nameFormatter";

import { QUANTITY_DECIMALS } from "@/components/global.constants";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";

const UsageHistoryDetails = ({ items, onEdit, onDelete, onDistribution, onAssociate }) => {
	const { filter } = useFilterStore();
	const unitMeasurement = filter?.unit_measurement || "kg_l";
	const canEdit = Boolean(onEdit);
	const canDelete = Boolean(onDelete);
	const canDistribute = Boolean(onDistribution);
	const canAssociate = Boolean(onAssociate);

	const columns = [
		{ key: "usage_time", label: "Hora Registro" },
		{
			key: "code",
			label: "Código",
			render: (value) => <span style={{ fontWeight: 600 }}>{value}</span>,
		},
		{
			key: "usage_quantity",
			label: "Cant. Utilizada",
			align: "right",
			render: (value, row) => {
				const unit = getUnitLabel(row.unit_measurement, unitMeasurement);
				let displayValue;
				if (row.unit_measurement === "u") {
					displayValue = formatNumber(value, 0);
				} else {
					const convertedValue = convertValue(value, row.unit_measurement, unitMeasurement);
					displayValue = formatNumber(truncateToDecimals(convertedValue, QUANTITY_DECIMALS), QUANTITY_DECIMALS);
				}
				return (
					<span>
						{displayValue}
						{unit && <span style={{ marginLeft: "4px" }}>{unit}</span>}
					</span>
				);
			},
		},
		{
			key: "cost_local",
			label: "Costo",
			align: "right",
			render: (value) => formatNumber(value, 2),
		},
		{
			key: "estate_name",
			label: "Destino",
			render: (value, row) => {
				const estateName = value || "";
				const parentName = row.estate_parent_name || "";

				if (!parentName && !estateName) return <span>-</span>;

				if (estateName === parentName) {
					const { abbreviated, full } = formatNameWithTooltip(estateName);
					return <CustomTooltip text={abbreviated} description={full} />;
				}

				const displayText = parentName && estateName ? `${estateName} - ${parentName}` : estateName || parentName;
				const { abbreviated, full } = formatNameWithTooltip(displayText);
				return <CustomTooltip text={abbreviated} description={full} />;
			},
		},
		{
			key: "business_center_name",
			label: "Centro Costo",
			render: (value) => {
				if (!value) return <span>-</span>;

				const centers = value.split(",").map((center) => center.trim());

				return (
					<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
						{centers.map((center, index) => (
							<CustomBadge key={index} variant="neutral" size="sm">
								{center}
							</CustomBadge>
						))}
					</div>
				);
			},
		},
		{
			key: "user_name",
			label: "Usuario",
			render: (value) => {
				if (!value) return <span>-</span>;
				const { abbreviated, full } = formatUser(value);
				return <CustomTooltip text={abbreviated} description={full} />;
			},
		},
		{
			key: "operator_name",
			label: "Operador",
			render: (value) => {
				if (!value) return <span>-</span>;

				const userNames = value.split(",").map((userName) => userName.trim());

				return (
					<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
						{userNames.map((userName, index) => {
							const { abbreviated } = formatUser(userName);
							return (
								<CustomBadge key={index} variant="neutral" size="sm">
									{abbreviated}
								</CustomBadge>
							);
						})}
					</div>
				);
			},
		},
	];

	if (canEdit || canDelete || canDistribute || canAssociate) {
		columns.push({
			key: "actions",
			label: "Acciones",
			align: "center",
			render: (value, row) => {
				const isReturnGeneric = canAssociate && row.is_generic && row.usage_quantity < 0;

				return (
					<div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
						{isReturnGeneric && (
							<CustomButton
								variant="secondary"
								size="sm"
								onClick={() => onAssociate(row.application_id)}
								startIcon={<Link size={16} />}
								title={"Asociar aplicación"}
								style={{ padding: "0.5rem" }}
							/>
						)}
						{canDistribute && (
							<CustomButton
								variant="secondary"
								size="sm"
								onClick={() => onDistribution(row.application_id)}
								startIcon={<ClipboardCheck size={16} />}
								title={"Revisar centros de costo"}
								style={{ padding: "0.5rem" }}
							/>
						)}
						{canEdit && (
							<CustomButton
								variant="primary"
								size="sm"
								onClick={() => onEdit(row.application_id)}
								startIcon={<Pencil size={16} />}
								title={"Editar aplicación"}
								style={{ padding: "0.5rem" }}
							/>
						)}
						{canDelete && (
							<CustomButton
								variant="danger"
								size="sm"
								onClick={() => onDelete(row.application_id)}
								startIcon={<Trash2 size={16} />}
								title={"Eliminar aplicación"}
								style={{ padding: "0.5rem" }}
							/>
						)}
					</div>
				);
			},
		});
	}

	return (
		<ExpandableTable
			title={`Detalle de Usos`}
			subtitle={`Total de Aplicaciones: ${items.length}`}
			columns={columns}
			data={items}
			rowKey="application_id"
			fullWidth={true}
		/>
	);
};

export default UsageHistoryDetails;
